import { Navigate, useLocation } from 'react-router-dom'
import { type ReactNode } from 'react'
import { useAuth } from './AuthContext'

type Props = {
  children: ReactNode
  /**
   * Si se indica, solo la audience especificada puede entrar.
   *  - 'user'  → cuentas de la tabla `users` (técnicos de tenant).
   *  - 'admin' → cuentas de la tabla `admins` (admin de plataforma Velt).
   */
  requireAudience?: 'user' | 'admin'
}

/**
 * Envoltura para rutas que requieren autenticación.
 *
 * - status 'loading'    → fragmento vacío (evita flash de redirect).
 * - status 'guest'      → redirect al login correspondiente conservando `state.from`.
 * - audience mismatch   → redirect al portal correcto según la audience del token.
 */
export function ProtectedRoute({ children, requireAudience }: Props) {
  const { state } = useAuth()
  const location = useLocation()

  if (state.status === 'loading') return null

  if (state.status === 'guest') {
    const loginPath = requireAudience === 'admin' ? '/admin/login' : '/login'
    return <Navigate to={loginPath} replace state={{ from: location.pathname }} />
  }

  if (requireAudience && state.user.audience !== requireAudience) {
    const fallback = state.user.audience === 'admin' ? '/admin' : '/dashboard'
    return <Navigate to={fallback} replace />
  }

  // Contraseña temporal — forzar cambio antes de acceder a cualquier ruta del portal de usuario.
  if (
    state.user.audience === 'user' &&
    state.user.must_change_password === true &&
    location.pathname !== '/change-password'
  ) {
    return <Navigate to="/change-password" replace />
  }

  return <>{children}</>
}
